import React, { useState, useEffect } from 'react';
import { 
  ListTodo, 
  ArrowUp, 
  ArrowDown, 
  Clock, 
  User, 
  Calendar,
  Plus,
  Filter,
  Search,
  ExternalLink,
  Star,
  AlertCircle,
  Target,
  TrendingUp,
  Zap,
  Users,
  Brain,
  Shield,
  Activity,
  BarChart3,
  Timer,
  CheckCircle2,
  Eye,
  Lightbulb
} from 'lucide-react';

interface BacklogItem {
  id: string;
  title: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'todo' | 'in-progress' | 'review' | 'done';
  assignee: string;
  dueDate: string;
  estimatedEffort: number;
  businessValue: number;
  riskScore: number;
  tags: string[];
  linkedExperiment?: string;
  jiraId?: string;
  // Enhanced strategic metrics
  strategicAlignment: number; // 1-100 alignment with strategic goals
  kpiImpact: string[]; // Which KPIs this impacts
  learningVelocity: number; // 1-100 how much learning this generates
  threatReadiness: number; // 1-100 how this addresses threats
  cycleTime: number; // Expected cycle time in days
  opportunityScore: number; // 1-100 opportunity alignment
  complianceDrift: number; // 1-100 compliance risk if delayed
  teamCapability: number; // 1-100 team readiness to execute
  fraudRiskDefence: number; // 1-100 fraud/risk mitigation value
  createdFrom?: string;
  sourceInsight?: any;
}

const Backlog: React.FC = () => {
  const [sortBy, setSortBy] = useState<'priority' | 'businessValue' | 'dueDate' | 'strategic' | 'impact'>('strategic');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItemKPIs, setSelectedItemKPIs] = useState<BacklogItem | null>(null);
  const [backlogItems, setBacklogItems] = useState<BacklogItem[]>([
    {
      id: '1',
      title: 'Implement AI-Powered Personalisation Engine',
      description: 'Develop and deploy machine learning algorithms for content personalisation based on user behaviour patterns',
      priority: 'critical',
      status: 'in-progress',
      assignee: 'Sarah Chen',
      dueDate: '2024-02-15',
      estimatedEffort: 21,
      businessValue: 95,
      riskScore: 30,
      tags: ['AI', 'Personalisation', 'Backend'],
      linkedExperiment: 'EXP-001',
      jiraId: 'ADAPT-1234',
      strategicAlignment: 98,
      kpiImpact: ['Customer Satisfaction', 'Revenue Growth', 'Market Share', 'Learning Velocity', 'Innovation Index'],
      learningVelocity: 92,
      threatReadiness: 85,
      cycleTime: 14,
      opportunityScore: 96,
      complianceDrift: 15,
      teamCapability: 88,
      fraudRiskDefence: 45
    },
    {
      id: '2',
      title: 'Mobile Checkout Flow Redesign',
      description: 'Streamline mobile checkout process to reduce friction and improve conversion rates',
      priority: 'high',
      status: 'review',
      assignee: 'Mike Rodriguez',
      dueDate: '2024-02-10',
      estimatedEffort: 13,
      businessValue: 85,
      riskScore: 15,
      tags: ['Mobile', 'UX', 'Frontend'],
      linkedExperiment: 'EXP-002',
      jiraId: 'ADAPT-1235',
      strategicAlignment: 89,
      kpiImpact: ['Conversion Rate', 'Customer Satisfaction', 'Mobile Revenue', 'User Experience Score'],
      learningVelocity: 78,
      threatReadiness: 70,
      cycleTime: 8,
      opportunityScore: 87,
      complianceDrift: 25,
      teamCapability: 95,
      fraudRiskDefence: 60
    },
    {
      id: '3',
      title: 'Voice Interface Integration',
      description: 'Add voice command capabilities to improve accessibility and user experience',
      priority: 'medium',
      status: 'todo',
      assignee: 'Emma Watson',
      dueDate: '2024-03-01',
      estimatedEffort: 34,
      businessValue: 70,
      riskScore: 45,
      tags: ['Voice', 'Accessibility', 'Innovation'],
      linkedExperiment: 'EXP-005',
      strategicAlignment: 75,
      kpiImpact: ['Innovation Index', 'Accessibility Score', 'Customer Satisfaction', 'Market Differentiation'],
      learningVelocity: 85,
      threatReadiness: 60,
      cycleTime: 21,
      opportunityScore: 72,
      complianceDrift: 35,
      teamCapability: 65,
      fraudRiskDefence: 30
    },
    {
      id: '4',
      title: 'Security Audit and Penetration Testing',
      description: 'Comprehensive security assessment and implementation of recommended fixes',
      priority: 'high',
      status: 'todo',
      assignee: 'David Kumar',
      dueDate: '2024-02-20',
      estimatedEffort: 8,
      businessValue: 60,
      riskScore: 85,
      tags: ['Security', 'Compliance', 'Backend'],
      jiraId: 'ADAPT-1236',
      strategicAlignment: 82,
      kpiImpact: ['Security Score', 'Compliance Rating', 'Risk Mitigation', 'Fraud Defence', 'Trust Index'],
      learningVelocity: 70,
      threatReadiness: 95,
      cycleTime: 5,
      opportunityScore: 45,
      complianceDrift: 90,
      teamCapability: 92,
      fraudRiskDefence: 98
    },
    {
      id: '5',
      title: 'Customer Feedback Analytics Dashboard',
      description: 'Create real-time dashboard for customer feedback analysis and sentiment tracking',
      priority: 'medium',
      status: 'todo',
      assignee: 'Lisa Park',
      dueDate: '2024-02-25',
      estimatedEffort: 16,
      businessValue: 75,
      riskScore: 20,
      tags: ['Analytics', 'Customer', 'Dashboard'],
      jiraId: 'ADAPT-1237',
      strategicAlignment: 86,
      kpiImpact: ['Customer Satisfaction', 'Learning Velocity', 'Product Quality', 'Behaviour Change Rate'],
      learningVelocity: 88,
      threatReadiness: 55,
      cycleTime: 12,
      opportunityScore: 78,
      complianceDrift: 20,
      teamCapability: 85,
      fraudRiskDefence: 25
    }
  ]);

  // Listen for new backlog items created from market insights
  useEffect(() => {
    const handleNewBacklogItem = (event: CustomEvent) => {
      const newItem = event.detail;
      setBacklogItems(prev => [newItem, ...prev]);
    };

    window.addEventListener('new-backlog-item-created', handleNewBacklogItem as EventListener);
    return () => {
      window.removeEventListener('new-backlog-item-created', handleNewBacklogItem as EventListener);
    };
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-900 text-red-300 border-red-600';
      case 'high': return 'bg-orange-900 text-orange-300 border-orange-600';
      case 'medium': return 'bg-yellow-900 text-yellow-300 border-yellow-600';
      case 'low': return 'bg-green-900 text-green-300 border-green-600';
      default: return 'bg-gray-900 text-gray-300 border-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo': return 'bg-gray-700 text-gray-300';
      case 'in-progress': return 'bg-blue-700 text-blue-300';
      case 'review': return 'bg-purple-700 text-purple-300';
      case 'done': return 'bg-green-700 text-green-300';
      default: return 'bg-gray-700 text-gray-300';
    }
  };

  const getPriorityScore = (priority: string) => {
    switch (priority) {
      case 'critical': return 4;
      case 'high': return 3;
      case 'medium': return 2;
      case 'low': return 1;
      default: return 0;
    }
  };

  const getCreatedFromIcon = (createdFrom?: string) => {
    switch (createdFrom) {
      case 'market-insight': return <Lightbulb className="w-4 h-4 text-yellow-400" />;
      case 'experiment': return <Activity className="w-4 h-4 text-blue-400" />;
      default: return null;
    }
  };

  // Enhanced strategic scoring algorithm
  const calculateStrategicScore = (item: BacklogItem) => {
    const weights = {
      strategicAlignment: 0.25,
      businessValue: 0.20,
      threatReadiness: 0.15,
      learningVelocity: 0.15,
      opportunityScore: 0.10,
      urgency: 0.10, // Based on due date and compliance drift
      teamReadiness: 0.05
    };

    const urgencyScore = Math.max(0, 100 - (new Date(item.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) + item.complianceDrift;
    
    return (
      item.strategicAlignment * weights.strategicAlignment +
      item.businessValue * weights.businessValue +
      item.threatReadiness * weights.threatReadiness +
      item.learningVelocity * weights.learningVelocity +
      item.opportunityScore * weights.opportunityScore +
      Math.min(100, urgencyScore) * weights.urgency +
      item.teamCapability * weights.teamReadiness
    );
  };

  // Resource efficiency calculation
  const calculateResourceEfficiency = (item: BacklogItem) => {
    return (item.businessValue * item.teamCapability) / (item.estimatedEffort * (item.riskScore + 1));
  };

  const sortedItems = [...backlogItems].sort((a, b) => {
    switch (sortBy) {
      case 'strategic':
        return calculateStrategicScore(b) - calculateStrategicScore(a);
      case 'impact':
        return calculateResourceEfficiency(b) - calculateResourceEfficiency(a);
      case 'priority':
        return getPriorityScore(b.priority) - getPriorityScore(a.priority);
      case 'businessValue':
        return b.businessValue - a.businessValue;
      case 'dueDate':
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      default:
        return calculateStrategicScore(b) - calculateStrategicScore(a);
    }
  });

  const filteredItems = sortedItems.filter(item => {
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         item.kpiImpact.some(kpi => kpi.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const statusCounts = {
    all: backlogItems.length,
    todo: backlogItems.filter(item => item.status === 'todo').length,
    'in-progress': backlogItems.filter(item => item.status === 'in-progress').length,
    review: backlogItems.filter(item => item.status === 'review').length,
    done: backlogItems.filter(item => item.status === 'done').length,
  };

  const getMetricColor = (value: number, reverse: boolean = false) => {
    if (reverse) {
      if (value <= 30) return 'text-green-400';
      if (value <= 60) return 'text-yellow-400';
      return 'text-red-400';
    } else {
      if (value >= 80) return 'text-green-400';
      if (value >= 60) return 'text-yellow-400';
      return 'text-red-400';
    }
  };

  const getKPIIcon = (kpi: string) => {
    if (kpi.toLowerCase().includes('customer')) return <Users className="w-3 h-3" />;
    if (kpi.toLowerCase().includes('revenue') || kpi.toLowerCase().includes('conversion')) return <TrendingUp className="w-3 h-3" />;
    if (kpi.toLowerCase().includes('security') || kpi.toLowerCase().includes('compliance')) return <Shield className="w-3 h-3" />;
    if (kpi.toLowerCase().includes('innovation') || kpi.toLowerCase().includes('learning')) return <Brain className="w-3 h-3" />;
    return <Target className="w-3 h-3" />;
  };

  // KPI Details Modal
  const KPIDetailsModal = ({ item, onClose }: { item: BacklogItem | null; onClose: () => void }) => {
    if (!item) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-700">
            <div>
              <h3 className="text-lg lg:text-xl font-bold text-white">KPI Impact Analysis</h3>
              <p className="text-gray-400 text-sm lg:text-base">{item.title}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colours"
            >
              <ExternalLink className="w-5 h-5 lg:w-6 lg:h-6" />
            </button>
          </div>

          <div className="p-4 lg:p-6">
            {/* Strategic Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-700 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-400">Strategic Alignment</div>
                <div className={`text-lg font-bold ${getMetricColor(item.strategicAlignment)}`}>
                  {item.strategicAlignment}%
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-400">Learning Velocity</div>
                <div className={`text-lg font-bold ${getMetricColor(item.learningVelocity)}`}>
                  {item.learningVelocity}%
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-400">Threat Readiness</div>
                <div className={`text-lg font-bold ${getMetricColor(item.threatReadiness)}`}>
                  {item.threatReadiness}%
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-400">Team Capability</div>
                <div className={`text-lg font-bold ${getMetricColor(item.teamCapability)}`}>
                  {item.teamCapability}%
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-400">Opportunity Score</div>
                <div className={`text-lg font-bold ${getMetricColor(item.opportunityScore)}`}>
                  {item.opportunityScore}%
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-400">Fraud Risk Defence</div>
                <div className={`text-lg font-bold ${getMetricColor(item.fraudRiskDefence)}`}>
                  {item.fraudRiskDefence}%
                </div>
              </div>
            </div>

            {/* KPI Impact Details */}
            <div>
              <h4 className="text-base lg:text-lg font-semibold text-white mb-4">
                KPI Impact Details ({item.kpiImpact.length} KPIs)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {item.kpiImpact.map((kpi, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-2">
                      {getKPIIcon(kpi)}
                      <span className="text-sm font-medium text-white">{kpi}</span>
                    </div>
                    <div className="text-xs text-gray-400">
                      {kpi.toLowerCase().includes('customer') && 'Directly impacts customer experience and satisfaction metrics'}
                      {kpi.toLowerCase().includes('revenue') && 'Affects revenue generation and business growth indicators'}
                      {kpi.toLowerCase().includes('security') && 'Influences security posture and compliance ratings'}
                      {kpi.toLowerCase().includes('learning') && 'Contributes to organisational learning and knowledge velocity'}
                      {kpi.toLowerCase().includes('innovation') && 'Drives innovation metrics and competitive advantage'}
                      {(!kpi.toLowerCase().includes('customer') && !kpi.toLowerCase().includes('revenue') && 
                        !kpi.toLowerCase().includes('security') && !kpi.toLowerCase().includes('learning') && 
                        !kpi.toLowerCase().includes('innovation')) && 'Strategic performance indicator'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategic Score Breakdown */}
            <div className="mt-6 p-4 bg-gray-700 rounded-lg">
              <h4 className="text-sm font-semibold text-white mb-3">Strategic Score Breakdown</h4>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-400 mb-1">
                  {Math.round(calculateStrategicScore(item))}
                </div>
                <div className="text-xs text-gray-400">
                  Calculated from Strategic Alignment (25%), Business Value (20%), 
                  Threat Readiness (15%), Learning Velocity (15%), and other factors
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Prioritised Backlog</h1>
          <p className="text-gray-400 text-sm lg:text-base">Strategic delivery management with real-time prioritisation</p>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-500">
            <span>Sorted by: {sortBy === 'strategic' ? 'Strategic Impact' : sortBy === 'impact' ? 'Resource Efficiency' : sortBy}</span>
            <span>•</span>
            <span>Focus: Most impactful, strategic items first</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4 lg:mt-0">
          <button className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg transition-colours flex items-center space-x-2 text-sm">
            <ExternalLink className="w-4 h-4" />
            <span>Sync JIRA</span>
          </button>
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg transition-colours flex items-center space-x-2 text-sm">
            <Plus className="w-4 h-4" />
            <span>Add Item</span>
          </button>
        </div>
      </div>

      {/* Strategic Metrics Overview */}
      <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-indigo-400" />
          <span>Strategic Impact Overview</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center">
            <div className="text-xs text-gray-400">Avg Strategic Alignment</div>
            <div className="text-xl font-bold text-green-400">
              {Math.round(backlogItems.reduce((sum, item) => sum + item.strategicAlignment, 0) / backlogItems.length)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">Learning Velocity</div>
            <div className="text-xl font-bold text-blue-400">
              {Math.round(backlogItems.reduce((sum, item) => sum + item.learningVelocity, 0) / backlogItems.length)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">Threat Readiness</div>
            <div className="text-xl font-bold text-purple-400">
              {Math.round(backlogItems.reduce((sum, item) => sum + item.threatReadiness, 0) / backlogItems.length)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">Team Capability</div>
            <div className="text-xl font-bold text-yellow-400">
              {Math.round(backlogItems.reduce((sum, item) => sum + item.teamCapability, 0) / backlogItems.length)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">Avg Cycle Time</div>
            <div className="text-xl font-bold text-cyan-400">
              {Math.round(backlogItems.reduce((sum, item) => sum + item.cycleTime, 0) / backlogItems.length)} days
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">Resource Efficiency</div>
            <div className="text-xl font-bold text-indigo-400">
              {Math.round(backlogItems.reduce((sum, item) => sum + calculateResourceEfficiency(item), 0) / backlogItems.length)}
            </div>
          </div>
        </div>
      </div>

      {/* Status Overview - Fixed Sizing and Alignment */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 lg:gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`flex flex-col items-center justify-center h-20 p-3 rounded-lg transition-colours ${
              filterStatus === status
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <div className="text-xl lg:text-2xl font-bold leading-none mb-1">{count}</div>
            <div className="text-xs lg:text-sm capitalize text-center leading-tight">
              {status.replace('-', ' ')}
            </div>
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search backlog items, KPIs, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none text-sm lg:text-base"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          className="bg-gray-800 text-white px-3 lg:px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none text-sm lg:text-base"
        >
          <option value="strategic">Sort by Strategic Impact</option>
          <option value="impact">Sort by Resource Efficiency</option>
          <option value="priority">Sort by Priority</option>
          <option value="businessValue">Sort by Business Value</option>
          <option value="dueDate">Sort by Due Date</option>
        </select>
      </div>

      {/* Backlog Items - Streamlined Cards */}
      <div className="space-y-4">
        {filteredItems.map((item, index) => (
          <div key={item.id} className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700 hover:border-gray-600 transition-colours">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              {/* Main Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {/* Experiment Number - Fixed Size */}
                    <div className="flex items-center justify-center w-8 h-6 bg-gray-700 text-gray-300 rounded text-xs font-medium">
                      #{index + 1}
                    </div>
                    <h3 className="text-base lg:text-lg font-semibold text-white">{item.title}</h3>
                    {item.linkedExperiment && (
                      <span className="px-2 py-1 bg-indigo-900 text-indigo-300 rounded-full text-xs flex items-center space-x-1">
                        <Star className="w-3 h-3" />
                        <span>{item.linkedExperiment}</span>
                      </span>
                    )}
                    {item.createdFrom && (
                      <div className="flex items-center space-x-1 px-2 py-1 bg-gray-700 rounded-full">
                        {getCreatedFromIcon(item.createdFrom)}
                        <span className="text-xs text-gray-400 capitalize">
                          {item.createdFrom.replace('-', ' ')}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Status and Priority Indicators - Fixed Sizes */}
                  <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                    <div className={`flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium border min-w-[80px] h-6 ${getPriorityColor(item.priority)}`}>
                      <span className="uppercase">{item.priority}</span>
                    </div>
                    <div className={`flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium min-w-[90px] h-6 ${getStatusColor(item.status)}`}>
                      <span className="uppercase">{item.status.replace('-', ' ')}</span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-400 text-sm mb-4">{item.description}</p>

                {/* Source insight link */}
                {item.sourceInsight && (
                  <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Created from Market Insight:</div>
                    <div className="text-sm text-yellow-400 font-medium">{item.sourceInsight.title}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {item.sourceInsight.category} • Confidence: {item.sourceInsight.confidence}%
                    </div>
                  </div>
                )}

                {/* Key Metrics - Simplified */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Strategic Score</div>
                    <div className={`text-sm font-bold ${getMetricColor(calculateStrategicScore(item))}`}>
                      {Math.round(calculateStrategicScore(item))}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Business Value</div>
                    <div className="text-sm font-bold text-green-400">{item.businessValue}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Risk Score</div>
                    <div className={`text-sm font-bold ${getMetricColor(item.riskScore, true)}`}>
                      {item.riskScore}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">KPIs Impacted</div>
                    <div className="text-sm font-bold text-indigo-400">{item.kpiImpact.length}</div>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {item.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Basic Info */}
                <div className="flex flex-wrap items-center gap-4 lg:gap-6 text-sm text-gray-400">
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>{item.assignee}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(item.dueDate).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{item.estimatedEffort}h</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Timer className="w-4 h-4" />
                    <span>{item.cycleTime} days</span>
                  </div>
                  {item.jiraId && (
                    <div className="flex items-center space-x-1">
                      <ExternalLink className="w-4 h-4" />
                      <span>{item.jiraId}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 mt-4 border-t border-gray-700 gap-3 sm:gap-0">
              <div className="flex flex-wrap items-center gap-2">
                {item.riskScore > 70 && (
                  <div className="flex items-center space-x-1 text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs">High Risk</span>
                  </div>
                )}
                {item.complianceDrift > 60 && (
                  <div className="flex items-center space-x-1 text-yellow-400">
                    <Shield className="w-4 h-4" />
                    <span className="text-xs">Compliance Risk</span>
                  </div>
                )}
                {new Date(item.dueDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                  <div className="flex items-center space-x-1 text-yellow-400">
                    <Clock className="w-4 h-4" />
                    <span className="text-xs">Due Soon</span>
                  </div>
                )}
                {item.strategicAlignment > 90 && (
                  <div className="flex items-center space-x-1 text-green-400">
                    <Target className="w-4 h-4" />
                    <span className="text-xs">Strategic Priority</span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button 
                  onClick={() => setSelectedItemKPIs(item)}
                  className="text-indigo-400 hover:text-indigo-300 transition-colours text-sm flex items-center space-x-1"
                >
                  <Eye className="w-4 h-4" />
                  <span>View KPIs</span>
                </button>
                <button className="text-gray-400 hover:text-white transition-colours">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button className="text-gray-400 hover:text-white transition-colours">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button className="text-indigo-400 hover:text-indigo-300 transition-colours text-sm">
                  Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <ListTodo className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No items found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* KPI Details Modal */}
      <KPIDetailsModal 
        item={selectedItemKPIs} 
        onClose={() => setSelectedItemKPIs(null)} 
      />
    </div>
  );
};

export default Backlog;